import type * as THREE from "three";
import type { AuraData, BackData, EmoteData, FaceData, HandData, HatData, OutfitData, PetData, SkinData } from "../../shared/avatar";
import { DEFAULT_SKIN } from "../../shared/avatar";
import type { Bin } from "./kit";

/** everything that makes up one figure */
export interface CharSpec {
  skin: SkinData;
  outfit?: OutfitData;
  hat?: HatData;
  face?: FaceData;
  back?: BackData;
  hand?: HandData;
  pet?: PetData;
  aura?: AuraData;
  emote?: EmoteData;
}

type Item = { id?: string; data: Record<string, any> } | undefined | null;
export type EquippedLike = Partial<Record<string, Item>> | null | undefined;

/** turns the equipped cosmetics (as the server sends them) into a figure spec; anything missing falls back to the default look */
export function specFromEquipped(eq: EquippedLike): CharSpec {
  const e = eq ?? {};
  const sd = e.skin?.data as Partial<SkinData> | undefined;
  const skin: SkinData = sd && sd.species ? ({ ...DEFAULT_SKIN, ...sd } as SkinData) : legacySkin(sd) ?? DEFAULT_SKIN;
  return {
    skin,
    outfit: e.outfit?.data as OutfitData | undefined,
    hat: e.hat?.data as HatData | undefined,
    face: e.accessory?.data as FaceData | undefined,
    back: e.back?.data as BackData | undefined,
    hand: e.hand?.data as HandData | undefined,
    pet: e.pet?.data as PetData | undefined,
    aura: e.aura?.data as AuraData | undefined,
    emote: e.emote?.data as EmoteData | undefined,
  };
}

/** skins saved before the 3D figures existed carried a flat `shape` – map them onto the closest species */
function legacySkin(sd: Record<string, any> | undefined): SkinData | null {
  if (!sd || !sd.shape) return null;
  const m: Record<string, SkinData["species"]> = { round: "blob", tiny: "blob", tall: "fox", robot: "robot", alien: "alien", knight: "kid", astro: "kid", cat: "cat", dragon: "dragon" };
  return { species: m[sd.shape] ?? "blob", body: sd.body ?? "#4cc9f0", accent: sd.accent ?? "#1b2a6b" };
}

/** the animation state the gear (wings, flames, tails) may read every frame */
export interface AnimState {
  /** 0..1 how fast the figure is walking right now */
  walk: number;
  /** seconds since the figure was built */
  t: number;
  /** 0..1 how "excited" the figure is (emotes, victory) */
  joy: number;
  /** true while a hop/jump is in the air */
  air: boolean;
}

export interface BuildCtx {
  bin: Bin;
  spec: CharSpec;
  bones: Map<string, THREE.Group>;
  /** create a bone under `parent` (or return the existing one) */
  bone(name: string, parent: string, x?: number, y?: number, z?: number): THREE.Group;
  /** a callback run every frame (wing flaps, tails, flames …) */
  tick(fn: (s: AnimState, dt: number) => void): void;
  /** register something to be disposed with the figure */
  own(...d: { dispose(): void }[]): void;
  rand: () => number;
  quality: "low" | "high";
  /** set by the builders that know better: the effective outfit, hair visibility under hats etc. */
  fx: { hideHair?: boolean; hairTop?: boolean; hairLift?: number; skipOutfit?: boolean; noHand?: boolean; hoodUp?: boolean };
  /** skin colour of the humanoid parts (hands, neck) */
  skinHex: string;
  /** the figure's main colour (fur, hull) */
  bodyHex: string;
}

export const HEAD_R = 0.25;
